<!--
  Topbar — Wave 5 (TWS-108) / Wave 8 (Portal Chrome Restyle)

  Frosted-glass shell rendered via `.tw-topbar`. All styling in
  `libs/ui/theme/src/styles/components/_topbar.scss`. Dropdown
  panels (notifications + user menu) consume the shared
  `<tw-popover>` primitive (TWS-122) — surface, anchor
  positioning, top-layer promotion, and light-dismiss all come
  from the primitive. Per-instance sizing/padding overrides ride
  on CSS custom properties so the bespoke inner markup keeps
  ownership of section layout.
-->
<header class="tw-topbar">

  <!-- Sidebar toggle / mobile hamburger -->
  <button
    type="button"
    class="tw-topbar__icon-button"
    [attr.aria-label]="layoutStore.viewportBelowBreakpoint() ? 'Open navigation' : 'Toggle sidebar'"
    [attr.aria-expanded]="layoutStore.viewportBelowBreakpoint() ? layoutStore.mobileOpen() : null"
    (click)="onHamburger()">
    <fa-icon [icon]="faBars"></fa-icon>
  </button>

  <div class="tw-topbar__spacer"></div>

  <!--
    Global search pill — opens the ⌘K navigation command palette.
    It is a button (not a text input): the palette owns the actual
    search field. The ⌘K / Ctrl+K shortcut is wired on a document
    keydown HostListener; clicking the pill opens the same palette.
  -->
<!--
  <button
    type="button"
    class="tw-topbar__search"
    aria-label="Search (Ctrl+K)"
    aria-keyshortcuts="Control+K Meta+K"
    (click)="openCommandPalette()">
    <fa-icon [icon]="faSearch" aria-hidden="true"></fa-icon>
    <span class="tw-topbar__search-placeholder">Search…</span>
    <kbd class="tw-topbar__search-kbd" aria-hidden="true">⌘K</kbd>
  </button>
-->

  <div class="tw-topbar__spacer"></div>

  <!--
    Help icon button + Notifications bell/popover removed for
    now — neither has a backend or feature implementation yet
    (no help system, no notification service). Restore via git
    when those features land (TWS-108 chrome restyle baseline).
  -->

  <!--
    Theme toggle (tri-state) — hidden on mobile to conserve space.
    Also gated to Twist platform admins (`OrganizationTypes.TwistAdmin`):
    non-admins are pinned to the dark theme by `LayoutStore` and do not
    see this control. The `LayoutStore` setters are no-ops for non-admins
    as a belt-and-braces guarantee.
  -->
  @if (isTwistAdmin()) {
    <button
      type="button"
      class="tw-topbar__icon-button tw-topbar__icon-button--desktop-only"
      [attr.aria-label]="themeToggleLabel()"
      [attr.title]="'Theme: ' + layoutStore.theme()"
      (click)="layoutStore.cycleTheme()">
      @switch (layoutStore.theme()) {
        @case ('dark')   { <fa-icon [icon]="faMoon"></fa-icon> }
        @case ('light')  { <fa-icon [icon]="faSun"></fa-icon> }
        @case ('system') { <fa-icon [icon]="faCircleHalfStroke"></fa-icon> }
      }
    </button>
  }

  <div class="tw-topbar__divider"></div>

  <!-- ================== User chip + dropdown ================== -->
  @if (authenticatedUser(); as user) {
    <tw-popover
      [(open)]="userOpen"
      triggerOn="click"
      placement="bottom">
      <button
        type="button"
        twPopoverTrigger
        class="tw-topbar__user"
        [class.tw-topbar__user--on]="userOpen()">
        <ui-avatar [email]="userEmail()" [username]="userName()" size="small" />
        <span class="tw-topbar__user-meta">
          <span class="tw-topbar__user-name">{{ userName() }}</span>
          <span class="tw-topbar__user-org">{{ userOrg() }}</span>
        </span>
        <fa-icon [icon]="faChevronDown" aria-hidden="true"></fa-icon>
      </button>

      <tw-popover-content
        class="tw-pop tw-pop--user"
        style="--twist-popover-max-inline-size: 320px; --twist-popover-min-inline-size: 320px; --twist-popover-padding: 0;"
        aria-label="Account">

        <div class="tw-pop__user-header">
          <ui-avatar [email]="userEmail()" [username]="userName()" size="large" />
          <div>
            <div class="tw-pop__user-name">{{ userName() }}</div>
            <div class="tw-pop__user-email">{{ userEmail() }}</div>
            <div class="tw-pop__user-role">
              <span class="tw-pop__role-badge">Admin</span>
              {{ organization()?.name ?? 'Twist' }}
            </div>
          </div>
        </div>

        @if (organization(); as currentOrg) {
          <div class="tw-pop__org">
            <div class="tw-pop__org-label">Organization</div>
            <button type="button" class="tw-pop__org-btn tw-pop__org-btn--active">
              <span class="tw-pop__org-mark" aria-hidden="true">{{ orgInitial() }}</span>
              <span style="flex:1;text-align:left;">
                <span class="tw-pop__org-name">{{ currentOrg.name }}</span>
                <span class="tw-pop__org-meta">{{ orgMeta() }}</span>
              </span>
              <fa-icon [icon]="faChevronRight" aria-hidden="true"></fa-icon>
            </button>
          </div>

          @if (hasMultipleOrganizations()) {
            <div class="tw-pop__menu" role="group" aria-label="Switch organization">
              @for (org of userOrganizations(); track org.key) {
                @if (org.key !== currentOrg.key) {
                  <button
                    type="button"
                    role="menuitem"
                    class="tw-pop__item"
                    (click)="switchOrganization(org.key)">
                    <fa-icon [icon]="faShuffle"></fa-icon>
                    {{ org.name }}
                  </button>
                }
              }
            </div>
          }
        }

        <div class="tw-pop__divider"></div>

        <div class="tw-pop__menu" role="group">
          <button
            type="button"
            role="menuitem"
            class="tw-pop__item"
            routerLink="/profile/my-account"
            (click)="closeUser()">
            <fa-icon [icon]="faUser"></fa-icon>
            Account settings
            <kbd>⌘,</kbd>
          </button>
          <button type="button" role="menuitem" class="tw-pop__item" disabled>
            <fa-icon [icon]="faShieldHalved"></fa-icon>
            Security &amp; 2FA
          </button>
          <button
            type="button"
            role="menuitem"
            class="tw-pop__item"
            routerLink="/profile/notifications"
            (click)="closeUser()">
            <fa-icon [icon]="faBell"></fa-icon>
            Notification preferences
          </button>
          <button type="button" role="menuitem" class="tw-pop__item" disabled>
            <fa-icon [icon]="faKey"></fa-icon>
            API tokens
          </button>
        </div>

        <div class="tw-pop__divider"></div>

        <div class="tw-pop__menu" role="group">
          <button type="button" role="menuitem" class="tw-pop__item" disabled>
            <fa-icon [icon]="faQuestionCircle"></fa-icon>
            Help &amp; docs
          </button>
          <button type="button" role="menuitem" class="tw-pop__item" disabled>
            <fa-icon [icon]="faGlobe"></fa-icon>
            Language · English
          </button>
        </div>

        <div class="tw-pop__divider"></div>

        <div class="tw-pop__menu" role="group">
          <button
            type="button"
            role="menuitem"
            class="tw-pop__item tw-pop__item--danger"
            (click)="logout()">
            <fa-icon [icon]="faSignOutAlt"></fa-icon>
            Sign out
          </button>
        </div>
      </tw-popover-content>
    </tw-popover>
  }
</header>
